import { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { useAppAuth } from '@/contexts/app-auth.context'
import { PageHeader } from '@/components/layout/PageHeader'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Skeleton } from '@/components/ui/skeleton'
import { Send } from 'lucide-react'
import {
  adminPublicApiClient,
  companyPublicApiClient,
  type PlatformWebhookDeliveryDetail,
  type PlatformWebhookDeliveryRow,
  type PlatformWebhookSubscriptionRow,
} from '@/lib/api/admin-public-api.client'
import {
  DATE_RANGES,
  type DateRangeValue,
  DetailField,
  FILTER_ALL,
  type FilterValue,
  PLATFORM_PAGE_SIZE,
  SectionTabs,
  sinceForRange,
  UNDERLINE_TABS_LIST,
  UNDERLINE_TABS_TRIGGER,
  formatTimestamp,
} from './platform-shared'
import { ROUTE_REGISTRY } from '@/lib/routes'
import { FloatingActionIslandProvider } from '@/contexts/FloatingActionIslandContext'
import { FloatingActionIsland } from '@/components/ui/FloatingActionIsland'
import { FloatingPagination } from '@/components/ui/FloatingPagination'

type StatusFilter = FilterValue<PlatformWebhookDeliveryRow['status']>

const STATUS_FILTERS: readonly StatusFilter[] = [
  FILTER_ALL,
  'SUCCESS',
  'FAILED',
  'DEAD_LETTER',
]

const DELIVERY_BADGE_CLASSES: Record<
  PlatformWebhookDeliveryRow['status'],
  string
> = {
  SUCCESS:
    'border-transparent bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-xs',
  FAILED:
    'border-transparent bg-amber-100 text-amber-700 hover:bg-amber-100 text-xs',
  DEAD_LETTER:
    'border-transparent bg-red-100 text-red-700 hover:bg-red-100 text-xs',
}

const DELIVERY_STATUS_LABELS: Record<
  PlatformWebhookDeliveryRow['status'],
  string
> = {
  SUCCESS: 'Success',
  FAILED: 'Failed',
  DEAD_LETTER: 'Dead-letter',
}

const MAX_DELIVERY_ATTEMPTS = 5

export function PlatformDeliveriesPage() {
  const { isInitialized, currentCompany, isSuperAdmin } = useAppAuth()
  const companyId = currentCompany?.id
  const [subscriptions, setSubscriptions] = useState<
    PlatformWebhookSubscriptionRow[] | null
  >(null)
  const [deliveries, setDeliveries] = useState<
    PlatformWebhookDeliveryRow[] | null
  >(null)
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(FILTER_ALL)
  const [dateRange, setDateRange] = useState<DateRangeValue>(FILTER_ALL)
  const [searchParams] = useSearchParams()
  const [subscriptionFilter, setSubscriptionFilter] = useState<
    FilterValue<string>
  >(() => searchParams.get('subscriptionId') ?? FILTER_ALL)
  const [loading, setLoading] = useState(false)
  const [detail, setDetail] = useState<PlatformWebhookDeliveryDetail | null>(
    null,
  )
  const [detailOpen, setDetailOpen] = useState(false)
  // Guards against a slow response for a previous filter landing after a
  // newer one and overwriting it
  const requestSeq = useRef(0)

  const loadDeliveries = useCallback(
    async (targetPage: number) => {
      if (!companyId) return
      const requestId = ++requestSeq.current
      try {
        setLoading(true)
        const params = {
          status: statusFilter === FILTER_ALL ? undefined : statusFilter,
          subscriptionId:
            subscriptionFilter === FILTER_ALL ? undefined : subscriptionFilter,
          since: sinceForRange(dateRange),
          limit: PLATFORM_PAGE_SIZE,
          page: String(targetPage),
        }
        const result = isSuperAdmin
          ? await adminPublicApiClient.listWebhookDeliveries({
              ...params,
              companyId,
            })
          : await companyPublicApiClient.listWebhookDeliveries(params)
        if (requestId !== requestSeq.current) return
        setDeliveries(result.items)
        setTotal(result.total)
        setPage(result.page)
      } catch (error) {
        if (requestId !== requestSeq.current) return
        toast.error(
          error instanceof Error ? error.message : 'Failed to load deliveries',
        )
        setDeliveries([])
      } finally {
        if (requestId === requestSeq.current) setLoading(false)
      }
    },
    [companyId, isSuperAdmin, statusFilter, dateRange, subscriptionFilter],
  )

  useEffect(() => {
    if (!isInitialized || !companyId) return
    const load = isSuperAdmin
      ? adminPublicApiClient.listWebhookSubscriptions({
          limit: '50',
          companyId,
        })
      : companyPublicApiClient.listWebhookSubscriptions({ limit: '50' })
    load
      .then(result => setSubscriptions(result.items))
      .catch(() => setSubscriptions([]))
  }, [isInitialized, companyId, isSuperAdmin])

  useEffect(() => {
    if (!isInitialized) return
    loadDeliveries(1)
  }, [isInitialized, loadDeliveries])

  const openDetail = async (row: PlatformWebhookDeliveryRow) => {
    setDetailOpen(true)
    setDetail(null)
    try {
      setDetail(
        isSuperAdmin
          ? await adminPublicApiClient.getWebhookDelivery(row.id)
          : await companyPublicApiClient.getWebhookDelivery(row.id),
      )
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to load delivery',
      )
      setDetailOpen(false)
    }
  }

  if (!isInitialized) return null

  return (
    <div className="h-full overflow-y-auto mt-16">
      <FloatingActionIslandProvider>
        <div className="space-y-6 p-6 pt-0">
          <div className="mt-6">
            <PageHeader title="Webhooks" />
          </div>
          <SectionTabs
            tabs={[
              {
                label: 'Usage',
                to: ROUTE_REGISTRY.WEBHOOKS.path,
                active: false,
              },
              {
                label: 'Endpoints',
                to: ROUTE_REGISTRY.WEBHOOKS_ENDPOINTS.path,
                active: false,
              },
              {
                label: 'Deliveries',
                to: ROUTE_REGISTRY.WEBHOOKS_DELIVERIES.path,
                active: true,
              },
            ]}
            actions={
              <>
                <Select
                  value={dateRange}
                  onValueChange={value => setDateRange(value as DateRangeValue)}
                >
                  <SelectTrigger className="h-9 w-[150px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DATE_RANGES.map(range => (
                      <SelectItem key={range.value} value={range.value}>
                        {range.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={subscriptionFilter}
                  onValueChange={setSubscriptionFilter}
                >
                  <SelectTrigger className="h-9 w-[220px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={FILTER_ALL}>All endpoints</SelectItem>
                    {(subscriptions ?? []).map(subscription => (
                      <SelectItem key={subscription.id} value={subscription.id}>
                        {subscription.url}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={statusFilter}
                  onValueChange={value =>
                    setStatusFilter(value as StatusFilter)
                  }
                >
                  <SelectTrigger className="h-9 w-[150px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_FILTERS.map(status => (
                      <SelectItem key={status} value={status}>
                        {status === FILTER_ALL ? 'All statuses' : status}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </>
            }
          />

          <Card className="w-full">
            <CardContent className="p-0">
              {deliveries === null ? (
                <div className="divide-y">
                  {[...Array(6)].map((_, index) => (
                    <div key={index} className="flex space-x-4 px-6 py-4">
                      <Skeleton className="h-4 w-40" />
                      <Skeleton className="h-4 w-28" />
                      <Skeleton className="h-4 w-24" />
                      <Skeleton className="h-4 w-32" />
                    </div>
                  ))}
                </div>
              ) : deliveries.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                  <Send className="h-8 w-8 text-gray-400 mb-4" />
                  <h3 className="mb-2 text-lg font-semibold">
                    No deliveries yet
                  </h3>
                  <p className="max-w-sm text-sm text-muted-foreground">
                    Every webhook delivery attempt lands here — successes,
                    retries, and dead-letters needing attention.
                  </p>
                </div>
              ) : (
                <div
                  className={cn(
                    'transition-opacity duration-200',
                    loading && 'pointer-events-none opacity-50',
                  )}
                >
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Attempted</TableHead>
                        <TableHead>Event</TableHead>
                        <TableHead>Endpoint</TableHead>
                        <TableHead>Attempt</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>HTTP</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {deliveries.map(delivery => (
                        <TableRow
                          key={delivery.id}
                          className="cursor-pointer"
                          onClick={() => openDetail(delivery)}
                        >
                          <TableCell className="whitespace-nowrap text-muted-foreground">
                            {formatTimestamp(delivery.createdAt)}
                          </TableCell>
                          <TableCell className="font-mono text-sm">
                            {delivery.event}
                          </TableCell>
                          <TableCell
                            className="max-w-[220px] truncate font-mono text-sm text-muted-foreground"
                            title={delivery.subscriptionUrl}
                          >
                            {delivery.subscriptionUrl}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {delivery.attemptNumber} / {MAX_DELIVERY_ATTEMPTS}
                          </TableCell>
                          <TableCell>
                            <Badge
                              className={
                                DELIVERY_BADGE_CLASSES[delivery.status]
                              }
                            >
                              {DELIVERY_STATUS_LABELS[delivery.status]}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {delivery.httpStatus ?? '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <FloatingPagination
                    componentId="deliveries-pagination"
                    itemType="deliveries"
                    total={total}
                    page={page}
                    pageSize={Number(PLATFORM_PAGE_SIZE)}
                    loading={loading}
                    onPage={loadDeliveries}
                  />
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <Sheet open={detailOpen} onOpenChange={setDetailOpen}>
          <SheetContent className="w-full overflow-y-auto p-6 sm:max-w-[560px]">
            {detail === null ? (
              <div className="space-y-3 pt-8">
                <Skeleton className="h-5 w-48" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-40 w-full" />
              </div>
            ) : (
              <>
                <SheetHeader>
                  <SheetTitle className="flex items-center gap-2 font-mono text-sm">
                    {detail.id}
                    <Badge className={DELIVERY_BADGE_CLASSES[detail.status]}>
                      {DELIVERY_STATUS_LABELS[detail.status]}
                    </Badge>
                  </SheetTitle>
                  <SheetDescription>
                    {detail.event} · {formatTimestamp(detail.createdAt)}
                  </SheetDescription>
                </SheetHeader>

                <Tabs defaultValue="response" className="mt-4">
                  <TabsList className={UNDERLINE_TABS_LIST}>
                    <TabsTrigger
                      value="response"
                      className={UNDERLINE_TABS_TRIGGER}
                    >
                      Response
                    </TabsTrigger>
                    <TabsTrigger
                      value="metadata"
                      className={UNDERLINE_TABS_TRIGGER}
                    >
                      Metadata
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="response" className="mt-3 space-y-3">
                    <div className="divide-y">
                      <DetailField label="HTTP status">
                        {detail.httpStatus ?? '—'}
                      </DetailField>
                    </div>
                    {detail.errorMessage && (
                      <div className="rounded-md border border-red-200 bg-red-50 p-3 text-xs dark:border-red-900 dark:bg-red-950">
                        <div className="break-all text-red-700 dark:text-red-400">
                          {detail.errorMessage}
                        </div>
                      </div>
                    )}
                    {detail.responseBody ? (
                      <pre className="max-h-[400px] overflow-auto rounded-md border bg-muted/50 p-3 font-mono text-xs leading-relaxed whitespace-pre-wrap break-all">
                        {detail.responseBody}
                      </pre>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        No response body captured.
                      </p>
                    )}
                  </TabsContent>

                  <TabsContent value="metadata" className="mt-3">
                    <div className="divide-y">
                      <DetailField label="Status" chip={false}>
                        <Badge
                          className={DELIVERY_BADGE_CLASSES[detail.status]}
                        >
                          {DELIVERY_STATUS_LABELS[detail.status]}
                        </Badge>
                      </DetailField>
                      <DetailField label="Delivery">{detail.id}</DetailField>
                      <DetailField label="Endpoint">
                        {detail.subscription.url}
                      </DetailField>
                      <DetailField label="Subscription">
                        {detail.subscriptionId}
                      </DetailField>
                      <DetailField label="Event">{detail.event}</DetailField>
                      <DetailField label="Attempt">
                        {`${detail.attemptNumber} / ${MAX_DELIVERY_ATTEMPTS}`}
                      </DetailField>
                      <DetailField label="Attempted at">
                        {formatTimestamp(detail.createdAt)}
                      </DetailField>
                    </div>
                  </TabsContent>
                </Tabs>
              </>
            )}
          </SheetContent>
        </Sheet>
        <FloatingActionIsland />
      </FloatingActionIslandProvider>
    </div>
  )
}
